import { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fetchGVIntelligence, type GVIntelligenceSource } from "@/lib/gvIntelligence";
import { fetchGraphLabels, fetchGraphEntitiesByLabel, queryLightRAG, type GraphEntity } from "@/lib/lightrag";

const GVIntelligenceOrb = lazy(() => import("./GVIntelligenceOrb"));

const LIGHT = {
  bg: "#ffffff",
  surface: "#f8faf8",
  surfaceHover: "#f0f4f0",
  border: "#e0e8e0",
  borderLight: "#d0dcd0",
  accent: "#014336",
  accentLight: "#026b54",
  accentGlow: "rgba(1,67,54,0.08)",
  green: "#014336",
  lime: "#026b54",
  text: "#1a1a1a",
  textDim: "#6b7b6b",
  textMuted: "#a0b0a0",
  white: "#ffffff",
  userBubble: "#014336",
  userBubbleText: "#ffffff",
  assistantBubble: "#f0f4f0",
  assistantBubbleText: "#1a1a1a",
  amber: "#b45309",
  cyan: "#0e7490",
  red: "#dc2626",
  headerBg: "#014336",
  headerText: "#ffffff",
  headerDim: "rgba(255,255,255,0.7)",
  inputBg: "#f8faf8",
  statusDot: "#22c55e",
  highlight: "rgba(1,67,54,0.12)",
};

const DARK = {
  bg: "#0a1210",
  surface: "#111c18",
  surfaceHover: "#162620",
  border: "#1e2e26",
  borderLight: "#2a3d32",
  accent: "#22c55e",
  accentLight: "#014336",
  accentGlow: "rgba(34,197,94,0.10)",
  green: "#22c55e",
  lime: "#b5ff00",
  text: "#e2e8e0",
  textDim: "#6b7f6b",
  textMuted: "#3d4f3d",
  white: "#f0f4f0",
  userBubble: "#014336",
  userBubbleText: "#e2e8e0",
  assistantBubble: "#111c18",
  assistantBubbleText: "#e2e8e0",
  amber: "#f59e0b",
  cyan: "#06b6d4",
  red: "#ef4444",
  headerBg: "#0d1a14",
  headerText: "#e2e8e0",
  headerDim: "rgba(226,232,224,0.5)",
  inputBg: "#111c18",
  statusDot: "#22c55e",
  highlight: "rgba(34,197,94,0.12)",
};

const FONT = {
  sans: "'DM Sans', 'SF Pro Display', -apple-system, sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
};

type Source = GVIntelligenceSource;

interface Message {
  role: "user" | "assistant";
  content: string;
  sources?: Source[];
  timestamp?: string;
  model?: string;
  graphEntities?: string[];
}

type Theme = typeof LIGHT;

function StatusBar({ status, t }: { status: string; t: Theme }) {
  const config: Record<string, { label: string; color: string; icon: string }> = {
    thinking: { label: "Denken...", color: t.amber, icon: "◆" },
    searching: { label: "Knowledge graph doorzoeken...", color: t.cyan, icon: "◉" },
    generating: { label: "Antwoord genereren...", color: t.accent, icon: "▶" },
  };
  const c = config[status];
  if (!c) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", margin: "0 16px 8px", borderRadius: 12, background: t.surface, border: `1px solid ${t.border}` }}>
      <span style={{ color: c.color, animation: "gvPulse 1.5s ease-in-out infinite", fontSize: 14 }}>{c.icon}</span>
      <span style={{ fontSize: 13, color: t.textDim, fontFamily: FONT.sans }}>{c.label}</span>
    </div>
  );
}

interface SourceRouteInfo {
  baseRoute: string;
  name: string;
  type: string;
}

function resolveSourceRoute(source: string): SourceRouteInfo | null {
  const s = source.trim();
  const patterns: [RegExp, string, string][] = [
    [/^Project:\s*/i, "/crm/projecten", "project"],
    [/^Projectnotitie:\s*/i, "/crm/projecten", "project_note"],
    [/^Charging Opportunity:\s*/i, "/crm/charging/opportunities", "opportunity"],
    [/^Opportunity notitie:\s*/i, "/crm/charging/opportunities", "opportunity"],
    [/^Organisatie:\s*/i, "/crm/organisaties", "organization"],
    [/^Contact:\s*/i, "/crm/contacten", "contact"],
    [/^Lead:\s*/i, "/crm/leads", "lead"],
    [/^Locatie:\s*/i, "/crm/locaties", "location"],
    [/^Subsidie:\s*/i, "/crm/subsidies", "subsidy"],
  ];
  for (const [pattern, route, type] of patterns) {
    if (pattern.test(s)) {
      const name = s.replace(pattern, "").trim();
      return { baseRoute: route, name, type };
    }
  }
  return null;
}

const entityIdCache = new Map<string, string | null>();

async function resolveEntityId(type: string, name: string): Promise<string | null> {
  if (!name) return null;
  const cacheKey = `${type}:${name}`;
  if (entityIdCache.has(cacheKey)) return entityIdCache.get(cacheKey)!;

  let data: { id: string } | null = null;
  try {
    switch (type) {
      case "project":
      case "project_note": {
        const res = await supabase.from("projects").select("id").ilike("name", `%${name}%`).is("deleted_at", null).limit(1).maybeSingle();
        data = res.data;
        break;
      }
      case "organization": {
        const res = await supabase.from("organizations").select("id").ilike("name", `%${name}%`).is("deleted_at", null).limit(1).maybeSingle();
        data = res.data;
        break;
      }
      case "contact": {
        // Search by first_name or last_name since there's no full_name column
        const parts = name.split(" ");
        const res = parts.length > 1
          ? await supabase.from("contacts").select("id").ilike("last_name", `%${parts[parts.length - 1]}%`).is("deleted_at", null).limit(1).maybeSingle()
          : await supabase.from("contacts").select("id").or(`first_name.ilike.%${name}%,last_name.ilike.%${name}%`).is("deleted_at", null).limit(1).maybeSingle();
        data = res.data;
        break;
      }
      case "opportunity": {
        const res = await supabase.from("charging_opportunities").select("id").ilike("name", `%${name}%`).is("deleted_at", null).limit(1).maybeSingle();
        data = res.data;
        break;
      }
      default:
        break;
    }
  } catch (e) {
    console.warn("Entity lookup failed:", type, name, e);
  }

  const id = data?.id ?? null;
  entityIdCache.set(cacheKey, id);
  return id;
}

function SourceBadge({ source, t, onNavigate }: { source: Source; t: Theme; onNavigate: (path: string) => void }) {
  const info = resolveSourceRoute(source.source);
  const [resolvedPath, setResolvedPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!info) return;
    // Types that support detail pages
    const detailTypes = ["project", "project_note", "organization", "contact", "opportunity"];
    if (!detailTypes.includes(info.type) || !info.name) {
      setResolvedPath(info.baseRoute);
      return;
    }
    setLoading(true);
    resolveEntityId(info.type, info.name).then((id) => {
      setResolvedPath(id ? `${info.baseRoute}/${id}` : info.baseRoute);
      setLoading(false);
    });
  }, [source.source]);

  const canClick = !!resolvedPath;

  return (
    <button
      onClick={() => canClick && onNavigate(resolvedPath!)}
      title={canClick ? `Ga naar ${source.source}` : source.source}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 6,
        background: t.accentGlow, border: `1px solid ${t.border}`,
        fontSize: 11, fontFamily: FONT.mono,
        cursor: canClick ? "pointer" : "default",
        transition: "all 0.2s",
        maxWidth: 220,
        textDecoration: "none",
      }}
    >
      <span style={{ color: t.accent, fontWeight: 600, flexShrink: 0 }}>[{source.index}]</span>
      <span style={{
        color: canClick ? t.accent : t.textDim,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textAlign: "left",
      }}>{source.source}</span>
      {loading && (
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: t.accent, opacity: 0.6,
          animation: "pulse 1s infinite",
          flexShrink: 0,
        }} />
      )}
      {!loading && canClick && <span style={{ color: t.accent, fontSize: 10, flexShrink: 0 }}>→</span>}
    </button>
  );
}

function GraphNodeChip({ name, t, highlighted }: { name: string; t: Theme; highlighted?: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 6,
        background: highlighted ? t.highlight : t.accentGlow,
        border: `1px solid ${highlighted ? t.accent : t.border}`,
        fontSize: 11, fontFamily: FONT.mono,
        color: t.accent,
        transition: "all 0.2s",
      }}
    >
      <span style={{ fontSize: 9 }}>◇</span>
      {name}
    </span>
  );
}

function MessageBubble({ message, t, onNavigate }: { message: Message; t: Theme; onNavigate: (path: string) => void }) {
  const isUser = message.role === "user";

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", padding: "4px 16px" }}>
      <div style={{
        maxWidth: "82%",
        padding: "12px 16px",
        borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
        background: isUser ? t.userBubble : t.assistantBubble,
        color: isUser ? t.userBubbleText : t.assistantBubbleText,
        fontFamily: FONT.sans,
        fontSize: 14,
        lineHeight: 1.6,
        border: isUser ? "none" : `1px solid ${t.border}`,
      }}>
        {message.content.split("\n").map((line: string, i: number) => {
          if (line.startsWith("### ")) {
            return (
              <div key={i} style={{ fontWeight: 700, fontSize: 14, marginTop: i > 0 ? 16 : 0, marginBottom: 6, color: isUser ? t.userBubbleText : t.accent }}>
                {line.replace("### ", "")}
              </div>
            );
          }
          if (line.includes("**")) {
            const parts = line.split(/\*\*(.*?)\*\*/g);
            return (
              <div key={i} style={{ marginBottom: 2 }}>
                {parts.map((part: string, j: number) =>
                  j % 2 === 1 ? (
                    <strong key={j} style={{ fontWeight: 600 }}>{part}</strong>
                  ) : (
                    <span key={j}>{part}</span>
                  )
                )}
              </div>
            );
          }
          if (line.trim().startsWith("* ") || line.trim().startsWith("- ")) {
            const indent = line.startsWith("    ") ? 20 : 0;
            return (
              <div key={i} style={{ display: "flex", gap: 8, marginLeft: indent, marginBottom: 2 }}>
                <span style={{ color: t.accent }}>·</span>
                <span>{line.trim().replace(/^[*\-]\s+/, "")}</span>
              </div>
            );
          }
          if (line.trim() === "") return <br key={i} />;
          return <div key={i} style={{ marginBottom: 2 }}>{line}</div>;
        })}

        {message.sources && message.sources.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${isUser ? "rgba(255,255,255,0.15)" : t.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: t.accent, fontFamily: FONT.mono, marginBottom: 6, letterSpacing: 1 }}>
              BRONNEN
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {message.sources.slice(0, 10).map((s: Source, i: number) => (
                <SourceBadge key={i} source={s} t={t} onNavigate={onNavigate} />
              ))}
              {message.sources.length > 10 && (
                <span style={{ fontSize: 11, color: t.textDim, padding: "2px 6px" }}>
                  +{message.sources.length - 10} meer
                </span>
              )}
            </div>
          </div>
        )}

        {message.graphEntities && message.graphEntities.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${isUser ? "rgba(255,255,255,0.15)" : t.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: t.cyan, fontFamily: FONT.mono, marginBottom: 6, letterSpacing: 1 }}>
              GRAPH NODES
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {message.graphEntities.slice(0, 8).map((name, i) => (
                <GraphNodeChip key={i} name={name} t={t} highlighted />
              ))}
              {message.graphEntities.length > 8 && (
                <span style={{ fontSize: 11, color: t.textDim, padding: "2px 6px" }}>
                  +{message.graphEntities.length - 8} meer
                </span>
              )}
            </div>
          </div>
        )}

        {message.timestamp && (
          <div style={{ fontSize: 10, color: isUser ? "rgba(255,255,255,0.5)" : t.textMuted, marginTop: 8, fontFamily: FONT.mono }}>
            {new Date(message.timestamp).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
            {message.model && ` · ${message.model}`}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Graph Explorer Panel ── */

function GraphExplorer({ t, highlightedNodes }: { t: Theme; highlightedNodes: string[] }) {
  const [labels, setLabels] = useState<string[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [entities, setEntities] = useState<GraphEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchGraphLabels()
      .then(setLabels)
      .catch(() => setError("Kan graph labels niet laden"))
      .finally(() => setLoading(false));
  }, []);

  const selectLabel = async (label: string) => {
    if (selectedLabel === label) {
      setSelectedLabel(null);
      setEntities([]);
      return;
    }
    setSelectedLabel(label);
    setLoadingEntities(true);
    try {
      const data = await fetchGraphEntitiesByLabel(label);
      setEntities(data);
    } catch {
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  const highlightSet = new Set(highlightedNodes.map(n => n.toLowerCase()));

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: t.surface, borderLeft: `1px solid ${t.border}`,
      overflow: "hidden",
    }}>
      <div style={{
        padding: "12px 14px", borderBottom: `1px solid ${t.border}`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>🔗</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: FONT.sans }}>
          Knowledge Graph
        </span>
        {labels.length > 0 && (
          <span style={{ fontSize: 10, color: t.textDim, fontFamily: FONT.mono, marginLeft: "auto" }}>
            {labels.length} labels
          </span>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
        {loading && (
          <div style={{ textAlign: "center", padding: 20, color: t.textDim, fontSize: 12 }}>
            Labels laden...
          </div>
        )}

        {error && (
          <div style={{ padding: 12, color: t.red, fontSize: 12, textAlign: "center" }}>
            {error}
          </div>
        )}

        {!loading && !error && labels.map((label) => (
          <div key={label}>
            <button
              onClick={() => selectLabel(label)}
              style={{
                width: "100%", textAlign: "left",
                padding: "8px 10px", borderRadius: 8,
                background: selectedLabel === label ? t.accentGlow : "transparent",
                border: "none", cursor: "pointer",
                fontSize: 12, fontFamily: FONT.sans,
                color: selectedLabel === label ? t.accent : t.text,
                fontWeight: selectedLabel === label ? 600 : 400,
                transition: "all 0.15s",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <span style={{ fontSize: 10 }}>{selectedLabel === label ? "▾" : "▸"}</span>
              {label}
            </button>

            {selectedLabel === label && (
              <div style={{ paddingLeft: 16, paddingBottom: 4 }}>
                {loadingEntities && (
                  <div style={{ padding: 8, fontSize: 11, color: t.textDim }}>Laden...</div>
                )}
                {!loadingEntities && entities.length === 0 && (
                  <div style={{ padding: 8, fontSize: 11, color: t.textDim }}>Geen entities</div>
                )}
                {!loadingEntities && entities.map((entity, i) => {
                  const entityName = entity.entity_name || entity.name || entity.id;
                  const isHighlighted = highlightSet.has(entityName.toLowerCase());
                  return (
                    <div
                      key={entity.id || i}
                      style={{
                        padding: "6px 8px", marginBottom: 2,
                        borderRadius: 6, fontSize: 11,
                        fontFamily: FONT.mono,
                        background: isHighlighted ? t.highlight : "transparent",
                        border: isHighlighted ? `1px solid ${t.accent}40` : "1px solid transparent",
                        color: isHighlighted ? t.accent : t.textDim,
                        transition: "all 0.2s",
                      }}
                      title={entity.description || entityName}
                    >
                      <div style={{ fontWeight: isHighlighted ? 600 : 400 }}>
                        {isHighlighted && <span style={{ marginRight: 4, fontSize: 9 }}>●</span>}
                        {entityName}
                      </div>
                      {entity.description && (
                        <div style={{
                          fontSize: 10, color: t.textMuted, marginTop: 2,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {entity.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main Chat Component ── */

export default function GVIntelligenceChat() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem("gv-intelligence-theme") === "dark"; } catch { return false; }
  });
  const [showGraph, setShowGraph] = useState(() => {
    try { return localStorage.getItem("gv-intelligence-graph") === "true"; } catch { return false; }
  });
  const [graphMode, setGraphMode] = useState<"cinematic" | "realistic">(() => {
    try { return (localStorage.getItem("gv-intelligence-mode") as "cinematic" | "realistic") || "cinematic"; } catch { return "cinematic"; }
  });
  const [orbPhase, setOrbPhase] = useState<"idle" | "searching" | "found" | "closing">("idle");
  const [progressiveHighlights, setProgressiveHighlights] = useState<string[]>([]);
  const orbVisible = orbPhase !== "idle";
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const loadedRef = useRef(false);

  const t = darkMode ? DARK : LIGHT;

  // Collect all highlighted graph entities from the last assistant message
  // Collect all highlighted graph entities — use progressive during search, final from last message
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === "assistant");
  const highlightedNodes = orbPhase === "searching" || orbPhase === "found"
    ? progressiveHighlights
    : (lastAssistantMsg?.graphEntities || []);

  const suggestions = [
    "Overzicht van alle actieve projecten",
    "Welke opportunities staan open?",
    "Wie zijn onze leveranciers?",
    "Status van de BESS projecten",
    "Welke subsidies zijn aangevraagd?",
  ];

  // Load chat history on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await (supabase
        .from("intelligence_chats") as any)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(100);
      if (data && data.length > 0) {
        setMessages(data.map((row: any) => ({
          role: row.role as "user" | "assistant",
          content: row.content,
          sources: row.sources || [],
          timestamp: row.created_at,
          model: row.model,
          graphEntities: row.graph_entities || [],
        })));
      }
    })();
  }, []);

  const persistMessage = useCallback(async (msg: Message) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await (supabase.from("intelligence_chats") as any).insert({
      user_id: user.id,
      role: msg.role,
      content: msg.content,
      sources: msg.sources || [],
      model: msg.model || null,
      graph_entities: msg.graphEntities || [],
    });
  }, []);

  const clearHistory = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await (supabase.from("intelligence_chats") as any).delete().eq("user_id", user.id);
    setMessages([]);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, status]);

  useEffect(() => {
    try { localStorage.setItem("gv-intelligence-theme", darkMode ? "dark" : "light"); } catch {}
  }, [darkMode]);

  useEffect(() => {
    try { localStorage.setItem("gv-intelligence-graph", showGraph ? "true" : "false"); } catch {}
  }, [showGraph]);

  useEffect(() => {
    try { localStorage.setItem("gv-intelligence-mode", graphMode); } catch {}
  }, [graphMode]);

  const toggleGraphMode = () => {
    setGraphMode(prev => {
      const next = prev === "cinematic" ? "realistic" : "cinematic";
      toast({
        title: next === "cinematic" ? "🎬 Cinematic Mode" : "🔬 Realistic Mode",
        description: next === "cinematic"
          ? "Visuele scan-animatie actief — toont een dramatische sweep door alle nodes"
          : "Alleen écht geraadpleegde nodes worden getoond — geen visuele overdrijving",
        duration: 3000,
      });
      return next;
    });
  };

  const sendQuery = async (queryText?: string) => {
    const query = queryText || input.trim();
    if (!query) return;

    setInput("");
    setError(null);
    setProgressiveHighlights([]);

    const userMsg: Message = { role: "user", content: query, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    persistMessage(userMsg);

    // Start orb animation
    setOrbPhase("searching");
    setStatus("thinking");
    const searchingTimeout = window.setTimeout(() => setStatus("searching"), 800);

    try {
      // Run LightRAG query in parallel for early entity extraction
      const lightragPromise = queryLightRAG(query, "hybrid")
        .then(result => {
          const entities = result.entities || [];
          if (entities.length > 0) {
            setProgressiveHighlights(prev => [...new Set([...prev, ...entities])]);
          }
          return entities;
        })
        .catch(() => [] as string[]);

      const data = await fetchGVIntelligence(query);

      window.clearTimeout(searchingTimeout);
      setStatus("generating");

      // Extract entity names from sources for graph highlighting
      const sourceEntityNames = (data.sources || [])
        .map(s => {
          const match = s.source.match(/^(?:Project|Organisatie|Contact|Lead|Locatie|Charging Opportunity):\s*(.+)/i);
          return match ? match[1].trim() : null;
        })
        .filter(Boolean) as string[];

      // Wait for LightRAG entities and combine
      const lightragEntities = await lightragPromise;
      const allEntities = [...new Set([...sourceEntityNames, ...lightragEntities])];

      // Update progressive highlights with final combined set
      setProgressiveHighlights(allEntities);

      const aiMsg: Message = {
        role: "assistant",
        content: data.answer || "Geen antwoord ontvangen.",
        sources: data.sources || [],
        timestamp: data.timestamp || new Date().toISOString(),
        model: data.model,
        graphEntities: allEntities,
      };

      // Show found phase briefly
      setOrbPhase("found");
      await new Promise(r => setTimeout(r, 1500));

      // Close orb
      setOrbPhase("closing");
      await new Promise(r => setTimeout(r, 600));

      setMessages((prev) => [...prev, aiMsg]);
      persistMessage(aiMsg);
      setOrbPhase("idle");
      setProgressiveHighlights([]);
    } catch (err: any) {
      window.clearTimeout(searchingTimeout);
      setError(`Verbinding mislukt: ${err?.message || "Onbekende fout"}`);
      setOrbPhase("closing");
      setTimeout(() => { setOrbPhase("idle"); setProgressiveHighlights([]); }, 600);
    } finally {
      window.clearTimeout(searchingTimeout);
      setStatus(null);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="gv-intelligence-shell" style={{ display: "flex", background: t.bg, fontFamily: FONT.sans, transition: "background 0.3s", position: "relative" }}>
      {/* Orb overlay — always mounted, visibility controlled by phase */}
      <div style={{ position: "absolute", inset: 0, zIndex: orbVisible ? 10 : -1 }}>
        <Suspense fallback={null}>
          <GVIntelligenceOrb
            visible={orbVisible}
            phase={orbPhase}
            highlightedNodes={highlightedNodes}
            mode={graphMode}
            statusText={status === "searching" ? "Knowledge graph doorzoeken..." : status === "thinking" ? "Denken..." : status === "generating" ? "Antwoord genereren..." : undefined}
          />
        </Suspense>
      </div>
      {/* Chat panel */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, position: "relative", zIndex: 1, opacity: orbVisible ? 0.3 : 1, transition: "opacity 0.4s ease-out" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${t.border}`, background: t.headerBg, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={toggleGraphMode}
              title={graphMode === "cinematic" ? "Mode: Cinematic — klik om te wisselen naar Realistic" : "Mode: Realistic — klik om te wisselen naar Cinematic"}
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: graphMode === "cinematic"
                  ? (darkMode ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.15)")
                  : (darkMode ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.2)"),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 14,
                color: graphMode === "cinematic"
                  ? "#f59e0b"
                  : (darkMode ? DARK.accent : "#ffffff"),
                fontFamily: FONT.mono,
                border: graphMode === "cinematic" ? "1px solid rgba(245,158,11,0.3)" : "none",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              GV
            </button>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: t.headerText }}>Intelligence</div>
              <div style={{ fontSize: 11, color: t.headerDim, fontFamily: FONT.mono }}>
                {graphMode === "cinematic" ? "🎬 Cinematic" : "🔬 Realistic"} · LightRAG · Gemini 2.5 Flash
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Graph toggle */}
            <button
              onClick={() => setShowGraph((g) => !g)}
              style={{
                width: 34, height: 34, borderRadius: 8,
                background: showGraph
                  ? (darkMode ? "rgba(34,197,94,0.2)" : "rgba(1,67,54,0.15)")
                  : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)"),
                border: showGraph ? `1px solid ${t.accent}40` : "none",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, color: t.headerText, transition: "background 0.2s",
              }}
              title={showGraph ? "Graph Explorer sluiten" : "Graph Explorer openen"}
            >🔗</button>
            {messages.length > 0 && (
              <button
                onClick={clearHistory}
                style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, color: t.headerText, transition: "background 0.2s",
                }}
                title="Chatgeschiedenis wissen"
              >🗑️</button>
            )}
            <button
              onClick={() => setDarkMode((d) => !d)}
              style={{
                width: 34, height: 34, borderRadius: 8,
                background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, color: t.headerText,
                transition: "background 0.2s",
              }}
              title={darkMode ? "Wissel naar light mode" : "Wissel naar dark mode"}
            >
              {darkMode ? "☀️" : "🌙"}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.statusDot, boxShadow: `0 0 8px ${t.statusDot}` }} />
              <span style={{ fontSize: 11, color: t.headerDim, fontFamily: FONT.mono }}>ONLINE</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", paddingTop: 16, paddingBottom: 16, transition: "background 0.3s" }}>
          {isEmpty && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40, gap: 24 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: t.text, marginBottom: 8 }}>
                  Greenvision Intelligence
                </div>
                <div style={{ fontSize: 14, color: t.textDim, maxWidth: 400 }}>
                  Stel vragen over projecten, contacten, leveranciers, offertes en meer.
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 500 }}>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendQuery(s)}
                    style={{
                      padding: "8px 14px", borderRadius: 20,
                      background: "transparent",
                      border: `1px solid ${t.border}`,
                      color: t.textDim, fontSize: 12,
                      fontFamily: FONT.sans, cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.color = t.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textDim; }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} t={t} onNavigate={navigate} />
          ))}

          {status && !orbVisible && <StatusBar status={status} t={t} />}

          {error && (
            <div style={{ margin: "8px 16px", padding: "14px 16px", borderRadius: 12, background: `${t.red}12`, border: `1px solid ${t.red}25`, fontSize: 13, fontFamily: FONT.sans }}>
              <div style={{ color: t.red, marginBottom: 8 }}>{error}</div>
              <button
                onClick={() => { setError(null); if (messages.length > 0) { const last = messages[messages.length - 1]; if (last.role === "user") sendQuery(last.content); } }}
                style={{ padding: "6px 14px", borderRadius: 8, background: t.red, color: "#fff", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: FONT.sans }}
              >
                Opnieuw proberen
              </button>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px 16px", borderTop: `1px solid ${t.border}`, background: t.bg, flexShrink: 0, transition: "background 0.3s" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, padding: "10px 14px", borderRadius: 14, background: t.inputBg, border: `1px solid ${t.border}`, transition: "all 0.2s" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendQuery();
                }
              }}
              placeholder="Stel een vraag over je projecten, contacten of data..."
              rows={1}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                color: t.text, fontSize: 14, fontFamily: FONT.sans,
                resize: "none", lineHeight: 1.5, padding: "2px 0",
              }}
              onInput={(e: any) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
            />
            <button
              onClick={() => sendQuery()}
              disabled={!input.trim() || !!status}
              style={{
                padding: "8px 18px", borderRadius: 10,
                background: input.trim() && !status ? t.accent : t.textMuted,
                border: "none", cursor: input.trim() && !status ? "pointer" : "default",
                color: input.trim() && !status ? "#fff" : t.textDim,
                fontSize: 13, fontWeight: 600, fontFamily: FONT.sans,
                transition: "all 0.2s",
              }}
            >
              Vraag
            </button>
          </div>
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 10, color: t.textMuted, fontFamily: FONT.mono }}>
            Antwoorden zijn gebaseerd op LightRAG knowledge graph · Data kan onvolledig zijn
          </div>
        </div>
      </div>

      {/* Graph Explorer side panel */}
      {showGraph && (
        <div style={{ width: 320, flexShrink: 0 }}>
          <GraphExplorer t={t} highlightedNodes={highlightedNodes} />
        </div>
      )}

      <style>{`
        .gv-intelligence-shell {
          box-sizing: border-box;
          width: 100%;
          height: calc(100vh - 64px);
          height: calc(100svh - 64px);
          overflow: hidden;
        }

        .gv-intelligence-shell * {
          box-sizing: border-box;
        }

        @keyframes gvPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
